import { Component, OnInit } from '@angular/core';

import {
  FollowPerson,
  FollowersService,
} from '../../../services/followers.service';

type Tab = 'followers' | 'blocked';

/**
 * SCRUM-102 — "ניהול עוקבים".
 *
 * Two tabs: who follows me, and who I have blocked. Blocking from the
 * followers tab removes the row immediately and drops them into the blocked
 * tab; unblocking does the reverse.
 */
@Component({
  selector: 'app-followers',
  templateUrl: './followers.component.html',
  styleUrls: ['./followers.component.scss'],
  standalone: false,
})
export class FollowersComponent implements OnInit {
  tab: Tab = 'followers';

  followers: FollowPerson[] = [];
  blocked: FollowPerson[] = [];

  loadingFollowers = true;
  loadingBlocked = false;
  blockedLoaded = false;

  followersHasNext = false;
  blockedHasNext = false;
  loadingMore = false;

  /** id of the row mid-request, so only that row shows a spinner. */
  busyId: number | null = null;
  error = '';

  /** The person awaiting block confirmation, or null. */
  confirming: FollowPerson | null = null;

  private followersPage = 1;
  private blockedPage = 1;

  constructor(private followersService: FollowersService) {}

  ngOnInit(): void {
    this.loadFollowers(1, true);
  }

  get currentList(): FollowPerson[] {
    return this.tab === 'followers' ? this.followers : this.blocked;
  }

  get currentLoading(): boolean {
    return this.tab === 'followers' ? this.loadingFollowers : this.loadingBlocked;
  }

  get emptyMessage(): string {
    return this.tab === 'followers'
      ? 'אין לך עוקבים עדיין'
      : 'לא חסמת אף משתמש';
  }

  get hasNext(): boolean {
    return this.tab === 'followers' ? this.followersHasNext : this.blockedHasNext;
  }

  selectTab(tab: Tab): void {
    this.tab = tab;
    // The blocked list is fetched on first visit rather than up front —
    // most people never open it.
    if (tab === 'blocked' && !this.blockedLoaded) {
      this.loadBlocked(1, true);
    }
  }

  displayName(p: FollowPerson): string {
    if (p.store && p.store.name) return p.store.name;
    return p.display_name || 'משתמש';
  }

  initials(p: FollowPerson): string {
    const t = this.displayName(p).trim();
    if (!t) return '?';
    const parts = t.split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  avatarOf(p: FollowPerson): string | null {
    if (p.store && p.store.logo) return p.store.logo;
    return p.avatar || null;
  }

  blockedDate(p: FollowPerson): string {
    if (!p.blocked_at) return '';
    const d = new Date(p.blocked_at);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL');
  }

  // ── loading ────────────────────────────────────────────────────────────

  private loadFollowers(page: number, replace: boolean): void {
    if (replace) this.loadingFollowers = true;
    else this.loadingMore = true;
    this.followersService.myFollowers(page).subscribe({
      next: (res) => {
        this.followers = replace ? res.results : this.followers.concat(res.results);
        this.followersHasNext = !!res.next;
        this.followersPage = page;
        this.loadingFollowers = false;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingFollowers = false;
        this.loadingMore = false;
        this.error = 'לא הצלחנו לטעון את רשימת העוקבים';
      },
    });
  }

  private loadBlocked(page: number, replace: boolean): void {
    if (replace) this.loadingBlocked = true;
    else this.loadingMore = true;
    this.followersService.blocked(page).subscribe({
      next: (res) => {
        this.blocked = replace ? res.results : this.blocked.concat(res.results);
        this.blockedHasNext = !!res.next;
        this.blockedPage = page;
        this.loadingBlocked = false;
        this.blockedLoaded = true;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingBlocked = false;
        this.blockedLoaded = true;
        this.loadingMore = false;
        this.error = 'לא הצלחנו לטעון את רשימת החסומים';
      },
    });
  }

  loadMore(): void {
    if (!this.hasNext || this.loadingMore) return;
    if (this.tab === 'followers') this.loadFollowers(this.followersPage + 1, false);
    else this.loadBlocked(this.blockedPage + 1, false);
  }

  // ── block / unblock ────────────────────────────────────────────────────

  askBlock(person: FollowPerson): void {
    this.confirming = person;
  }

  cancelBlock(): void {
    this.confirming = null;
  }

  confirmBlock(): void {
    const person = this.confirming;
    this.confirming = null;
    if (!person) return;

    this.busyId = person.id;
    this.error = '';

    // Optimistic: the row leaves the followers list at once and appears in
    // blocked. Restored on failure.
    const index = this.followers.findIndex((p) => p.id === person.id);
    if (index >= 0) this.followers.splice(index, 1);

    this.followersService.block(person.id).subscribe({
      next: () => {
        this.busyId = null;
        // Only push into the blocked list if it has been loaded — otherwise
        // the first visit will fetch it and this row would be duplicated.
        if (this.blockedLoaded && !this.blocked.some((p) => p.id === person.id)) {
          this.blocked.unshift({ ...person, blocked_at: new Date().toISOString() });
        }
      },
      error: () => {
        this.busyId = null;
        if (index >= 0) this.followers.splice(index, 0, person);
        this.error = 'החסימה נכשלה, נסה שוב';
        setTimeout(() => (this.error = ''), 4000);
      },
    });
  }

  unblock(person: FollowPerson): void {
    this.busyId = person.id;
    this.error = '';

    const index = this.blocked.findIndex((p) => p.id === person.id);
    if (index >= 0) this.blocked.splice(index, 1);

    this.followersService.unblock(person.id).subscribe({
      next: () => {
        this.busyId = null;
        // Deliberately does NOT restore them to the followers list: unblocking
        // permits a follow, it does not recreate one. Matches the backend.
      },
      error: () => {
        this.busyId = null;
        if (index >= 0) this.blocked.splice(index, 0, person);
        this.error = 'ביטול החסימה נכשל, נסה שוב';
        setTimeout(() => (this.error = ''), 4000);
      },
    });
  }
}
